import { Link } from "react-router-dom";
import { Gauge, Gem, Signal, Sparkles, Info, ArrowUpRight } from "lucide-react";
import { entityTrack } from "@/lib/entities";
import { ModuleShell, ModuleDelayed, UpdatedStamp, INNER_CARD } from "./shell";

/* 04 — Layer Index Context (approved frame, sparklines OMITTED by founder
   directive — no chart-shaped decoration where no history data exists).
   Three-level hierarchy: Layer Index → DeFi Theme → Entity Signal.
   Never an entity Layer Index score / contribution figure. */

const INDEX_COLORS: Record<string, string> = {
  "Extreme Fear": "#DC2626", "Fear": "#F87171", "Neutral": "#A3A3A3",
  "Greed": "#34D399", "Extreme Greed": "#10B981",
};

const Tile = ({ icon: Icon, iconColor, label, value, valueColor, sub, updated, testid }: any) => (
  <div className={`${INNER_CARD} px-[15px] py-[14px] md:px-[17px] md:py-[16px]`} data-testid={testid}>
    <p className="nl-geist inline-flex items-center gap-[8px] text-[9px] md:text-[10px] leading-[13px] font-semibold tracking-[0.07em] uppercase"
      style={{ color: iconColor }}>
      <Icon aria-hidden className="h-[13px] w-[13px]" /> {label}
    </p>
    <span aria-hidden className="block h-[2px] w-[34px] rounded-full mt-[10px]"
      style={{ background: `linear-gradient(90deg, ${iconColor}, transparent)` }} />
    <p className="nl-sg text-[30px] leading-[36px] md:text-[34px] md:leading-[40px] font-medium mt-[10px]" style={{ color: valueColor }}>
      {value}
    </p>
    {sub && <p className="nl-geist text-[10.5px] md:text-[11px] leading-[15px] text-[#9499ad] mt-[4px]">{sub}</p>}
    <div className="mt-[12px]"><UpdatedStamp iso={updated} /></div>
  </div>
);

export const EntityLayerIndexModule = ({ overview }: { overview: any }) => {
  const li = overview.layer_index;
  const name = overview.identity.name;
  if (!li) return null;
  if (li.error)
    return <ModuleDelayed num="4" title="Layer Index Context" sub={`${name} & Market Sentiment`} testid="entity-layerindex-delayed"
      message="Layer Index context is temporarily unavailable. Nothing is estimated in its place." />;

  const idx = li.index || {};
  const theme = li.defi_theme;
  const sig = li.entity_signal || {};
  const entityId = overview.identity.entity_id;

  const themeWord = theme ? (theme.dir === "up" ? "Positive" : "Negative") : "Quiet";
  const themeColor = theme ? (theme.dir === "up" ? "#1fe09c" : "#f0445e") : "#9499ad";
  const sigWord = sig.net > 0 ? "Bullish" : sig.net < 0 ? "Bearish" : "Neutral";
  const sigColor = sig.net > 0 ? "#1fe09c" : sig.net < 0 ? "#f0445e" : "#9499ad";
  const idxColor = INDEX_COLORS[idx.label] || "#A3A3A3";

  const themePhrase = theme
    ? `${theme.dir === "up" ? "positive" : "negative"} (${theme.label})`
    : "quiet — DeFi is not among the index's active themes right now";
  const narrative = sig.stories_24h === 0
    ? `No ${name}-matching stories registered in the sentiment engine over the last 24 hours, so the entity signal is neutral by absence of coverage. The DeFi theme is currently ${themePhrase}, and the broader Layer Index sits at ${idx.value ?? "—"} (${idx.label ?? "—"}).`
    : `${name} coverage currently reads ${sigWord.toLowerCase()} (${sig.bull_signals} bullish vs ${sig.bear_signals} bearish signals across ${sig.stories_24h} stor${sig.stories_24h === 1 ? "y" : "ies"}). The DeFi theme is ${themePhrase}, and the broader Layer Index sits at ${idx.value ?? "—"} (${idx.label ?? "—"}).`;

  return (
    <ModuleShell num="4" title="Layer Index Context" sub={`${name} & Market Sentiment`} testid="entity-layerindex"
      right={
        <Link to="/layer-index" data-testid="layerindex-view-link"
          onClick={() => entityTrack("layer_index_click", entityId, { from: "entity_module" })}
          className="nl-geist inline-flex items-center gap-[6px] text-[11px] md:text-[12px] font-semibold text-[#f6f7fc] border border-[#2b2e45] rounded-[10px] px-[13px] py-[8px] hover:border-[#22d3ee]/60 hover:text-[#22d3ee] transition-colors">
          View Layer Index <ArrowUpRight className="h-[13px] w-[13px]" />
        </Link>
      }
      footNote={
        <span data-testid="layerindex-methodology-note">
          The Layer Index blends NewsLayer headline sentiment, BTC &amp; ETH market momentum and
          breaking-news stability, recomputed roughly every 30 minutes.
        </span>
      }
      footRight={
        <Link to="/layer-index"
          onClick={() => entityTrack("layer_index_click", entityId, { from: "entity_module_methodology" })}
          className="nl-geist text-[10px] md:text-[11px] font-semibold text-[#22d3ee] hover:text-white transition-colors whitespace-nowrap">
          Learn more about the methodology →
        </Link>
      }>
      <p className="sr-only" data-testid="layerindex-sr-summary">
        {name}'s entity signal is {sigWord}. The DeFi theme is {themeWord}. The broader Layer Index
        is {idx.value ?? "unavailable"}, {idx.label ?? ""}.
      </p>

      <div className="grid sm:grid-cols-3 gap-[10px] md:gap-[14px]">
        <Tile icon={Gauge} iconColor="#b79aff" label="Layer Index (Overall)"
          value={idx.value ?? "—"} valueColor={idxColor} sub={idx.label || "—"}
          updated={idx.computed_at} testid="layerindex-tile-overall" />
        <Tile icon={Gem} iconColor="#22d3ee" label="DeFi Theme"
          value={themeWord} valueColor={themeColor}
          sub={theme ? theme.label : "Not among active index themes"}
          updated={idx.computed_at} testid="layerindex-tile-theme" />
        <Tile icon={Signal} iconColor="#8b5cff" label={`${name} Entity Signal`}
          value={sigWord} valueColor={sigColor}
          sub={sig.stories_24h === 0 ? "No matching stories in the last 24h"
            : `${sig.stories_24h} stor${sig.stories_24h === 1 ? "y" : "ies"} · ${sig.bull_signals} bull / ${sig.bear_signals} bear signals`}
          updated={li.fetched_at} testid="layerindex-tile-signal" />
      </div>

      <div className="grid lg:grid-cols-2 gap-[14px] lg:gap-[20px] mt-[14px] items-stretch">
        <div className={`${INNER_CARD} px-[16px] py-[15px]`} data-testid="layerindex-narrative">
          <p className="nl-geist inline-flex items-center gap-[8px] text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
            <Sparkles aria-hidden className="h-[13px] w-[13px] text-[#b79aff]" /> What This Means
          </p>
          <p className="nl-geist text-[12px] md:text-[12.5px] leading-[19px] text-[#9499ad] mt-[11px]">{narrative}</p>
        </div>
        <div className={`${INNER_CARD} px-[16px] py-[15px]`} data-testid="layerindex-about">
          <p className="nl-geist inline-flex items-center gap-[8px] text-[10px] md:text-[11px] leading-[14px] font-semibold tracking-[0.06em] uppercase text-[#f6f7fc]">
            <Info aria-hidden className="h-[13px] w-[13px] text-[#22d3ee]" /> About This Data
          </p>
          <ul className="mt-[11px] space-y-[8px]">
            {["Layer Index: NewsLayer's proprietary 0–100 market-mood index",
              "DeFi Theme: a narrative signal inside the index's sentiment component",
              `${name} Signal: entity-level directional sentiment from the same engine`,
              `Not a contribution figure or Layer Index score of the ${name} protocol`].map((t, i) => (
              <li key={i} className="nl-geist text-[11.5px] leading-[16px] text-[#9499ad] flex gap-[8px]">
                <span aria-hidden className="text-[#8b5cff] mt-[1px]">•</span>{t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModuleShell>
  );
};
